/**
 * Reglas de la ficha de puesto (levantamiento de canal).
 *
 * Las preguntas y su obligatoriedad salen de `arranque-campo.xlsx`, pestaña «Formulario», que es
 * la fuente. Vive acá y no dentro de la pantalla porque son las reglas que deciden si un dato
 * entra bien o entra roto, y una regla que solo existe dentro de un componente no se puede
 * verificar. La pantalla pinta; esto decide.
 */

export type Presencia = 'presente' | 'no_trabaja' | 'quiebre';

/** Las tres, con el texto exacto que ve el auditor. El orden es el de la cuadrícula. */
export const PRESENCIAS: { valor: Presencia; label: string }[] = [
  { valor: 'presente', label: 'Lo tiene' },
  { valor: 'no_trabaja', label: 'No lo trabaja' },
  { valor: 'quiebre', label: 'Lo trabaja pero está agotado' },
];

/**
 * `giro` es la pregunta VIEJA («¿qué vende?», una sola opción). La reemplaza `categorias`, que
 * admite varias y con la que la ficha decide qué secciones muestra.
 *
 * No se borra: la columna sigue existiendo, el CHECK `levantamiento_visita_completa` todavía la
 * exige en una visita medida, y hay 18 visitas cargadas que solo tienen esto. Ver
 * `giroDesdeCategorias`, que es lo que permite que una pantalla deje de preguntarla sin que el
 * servidor rechace la ficha.
 */
export const GIROS = [
  { valor: 'papel', label: 'Solo papel' },
  { valor: 'panal', label: 'Solo pañal y toallas' },
  { valor: 'ambos', label: 'Ambos' },
  { valor: 'abarrotes', label: 'Abarrotes con algo de esto' },
] as const;

// ---------------------------------------------------------------------------
// Categorías de producto — el centro del rediseño
// ---------------------------------------------------------------------------

/**
 * Qué categoría de producto se mide. Es la llave de TODO lo demás.
 *
 * POR QUÉ EXISTE. Hasta la 0060 el formulario le preguntaba las 6 marcas a todos los puestos.
 * Contra producción, de las 14 visitas MEDIDAS de la campaña real, 3 declararon vender solo papel y
 * 1 abarrotes: al 29% se le preguntó por Babysec y Huggies para nada, y lo que salió de ahí fue un
 * «no lo trabaja» que mezcla al puesto que decidió no tener la marca con el que ni siquiera está en
 * ese negocio. Son dos hechos opuestos —uno es una oportunidad comercial y el otro no— y fundirlos
 * es de lo poco que no se puede recuperar después.
 */
export type CategoriaProducto = 'papel_higienico' | 'panales' | 'toallas_higienicas';

/** Las tres, con el texto exacto que ve la auditora. El orden es el de la ficha. */
export const CATEGORIAS: { valor: CategoriaProducto; label: string }[] = [
  { valor: 'papel_higienico', label: 'Papel higiénico' },
  { valor: 'panales', label: 'Pañales' },
  { valor: 'toallas_higienicas', label: 'Toallas higiénicas' },
];

export function etiquetaCategoria(c: CategoriaProducto): string {
  return CATEGORIAS.find((x) => x.valor === c)?.label ?? c;
}

/**
 * ¿Es una de las tres? Se pregunta al LEER del servidor, no al escribir.
 *
 * `categorias` llega como `text[]` de PostgREST, o sea `string[]` para TypeScript: sin esta guarda
 * habría que hacer un `as CategoriaProducto[]`, y una aserción es una promesa a mano que nadie
 * comprueba. El día que alguien agregue una categoría en la base y no acá, la aserción la dejaría
 * pasar y reventaría más adelante, lejos del origen; la guarda la descarta donde se puede ver.
 */
export function esCategoriaProducto(v: string): v is CategoriaProducto {
  return CATEGORIAS.some((c) => c.valor === v);
}

/** Las categorías conocidas de una lista cruda del servidor, en su orden y sin repetidas. */
export function categoriasDesdeServidor(
  crudas: readonly string[] | null | undefined,
): CategoriaProducto[] {
  const salida: CategoriaProducto[] = [];
  for (const v of crudas ?? []) {
    if (esCategoriaProducto(v) && !salida.includes(v)) salida.push(v);
  }
  return salida;
}

/**
 * El `giro` que corresponde a estas categorías, para que la pantalla no tenga que preguntar dos
 * veces lo mismo.
 *
 * NO es cosmético: el CHECK de tabla `levantamiento_visita_completa` sigue exigiendo `giro` en toda
 * visita `medido`, así que una ficha que solo preguntara categorías sería rechazada por el servidor.
 * Con esto la pantalla nueva pregunta UNA vez y el payload sale completo.
 *
 * `abarrotes` no se puede derivar y no se pierde nada: era «Abarrotes con algo de esto», que no
 * dice QUÉ de esto — o sea, exactamente la ambigüedad que las categorías vienen a quitar. Una
 * pantalla que todavía pregunte `giro` a mano manda el suyo y este derivado no se usa.
 */
export function giroDesdeCategorias(categorias: readonly CategoriaProducto[]): string | null {
  const papel = categorias.includes('papel_higienico');
  // Toallas higiénicas cae del lado del pañal porque así lo dice la etiqueta que el equipo ya
  // conoce: «Solo pañal y toallas».
  const otras = categorias.includes('panales') || categorias.includes('toallas_higienicas');
  if (papel && otras) return 'ambos';
  if (papel) return 'papel';
  if (otras) return 'panal';
  return null;
}

export const CLIENTES = [
  { valor: 'bodeguero', label: 'Bodeguero que revende' },
  { valor: 'submayorista', label: 'Submayorista' },
  { valor: 'restaurante_hostal', label: 'Restaurante u hostal' },
  { valor: 'consumidor_final', label: 'Consumidor final' },
  { valor: 'no_sabe', label: 'No sabe' },
] as const;

/**
 * Pregunta 16: **opción única + Otra**, no texto libre.
 *
 * Es "share de recomendación", o sea que se CUENTA. Con texto libre, "Babysec", "babysec" y
 * "baby sec" son tres marcas distintas y el indicador hay que limpiarlo a mano antes de poder
 * leerlo — que es exactamente lo que pasaba en la herramienta anterior.
 */
export const MARCAS_RECOMENDABLES = [
  'Babysec', 'Huggies', 'Elite', 'Suave', 'La más barata',
] as const;

/** Valor que activa el campo de texto de "Otra". */
export const RECOMENDADA_OTRA = 'Otra';

/**
 * Valor del desplegable de mercado que abre el campo para escribirlo a mano.
 *
 * NO crea un `pdv`: el maestro de puntos de venta se administra aparte, y dejar que el campo lo
 * llene produciría veinte variantes del mismo mercado escritas distinto. Queda como texto hasta
 * que alguien lo dé de alta y una las variantes — igual que un hallazgo de marca nueva.
 */
export const MERCADO_OTRO = 'otro';

/** La clave con la que se agrupa un mercado, sea de la lista o escrito a mano. */
export function claveDeMercado(pdvId: string, mercadoLibre: string): string {
  return pdvId !== '' ? pdvId : `libre:${mercadoLibre.trim().toLowerCase()}`;
}

// ---------------------------------------------------------------------------
// Qué campañas se le ofrecen a quién
// ---------------------------------------------------------------------------

/**
 * Una campaña de levantamiento como la ve un desplegable.
 *
 * `esBancoPruebas` viaja hasta la pantalla en vez de filtrarse en la consulta porque la regla NO es
 * "esconderla siempre": es esconderla a quien solo trabaja en campo y mostrársela marcada a los
 * roles de oficina. Filtrar en el servidor haría imposible lo segundo.
 */
export interface CampaniaElegible {
  id: string;
  nombre: string;
  esBancoPruebas: boolean;
}

/**
 * ¿Esta cuenta solo trabaja en campo? (o sea: su único rol es `recurso`)
 *
 * Se pregunta por lo que la cuenta NO tiene y no por una lista de roles de oficina, para que una
 * pantalla nueva o un rol nuevo no queden fuera por olvido. Sin roles todavía cargados devuelve
 * `true`, y ese lado del error es el correcto: mientras no se sepa quién es, se le esconde el banco
 * de pruebas. Enseñárselo a un auditor por medio segundo es exactamente el accidente que esto
 * existe para impedir.
 */
export function soloTrabajaEnCampo(roles: readonly string[]): boolean {
  return !roles.some((r) => r !== 'recurso');
}

/** Cómo se marca en la lista una campaña de ensayo. Va en el NOMBRE, no en un ícono aparte. */
export const SUFIJO_BANCO_PRUEBAS = ' — BANCO DE PRUEBAS';

/**
 * Las campañas que se le ofrecen a esta persona, ya rotuladas.
 *
 * ⚠️ POR QUÉ EXISTE — el accidente del 19-ago-2026. `listCampaniasLevantamiento` filtraba por tipo,
 * estado y archivado, pero **no excluía el banco de pruebas**: la exclusión sí estaba en
 * Planificación y en el tablero de Hoy (0041), y acá nunca se puso. El auditor tuvo «Demo
 * levantamiento de canal» como una opción más del desplegable, la eligió, y las **18 visitas reales
 * de Túpac Amaru y Naranjal** quedaron archivadas en una campaña de ensayo.
 *
 * NO se resuelve borrando la demo: el banco de pruebas existe a propósito —para ensayar el
 * formulario y para mostrárselo a un cliente sin ensuciar el dato real— y esa es una decisión
 * cerrada del proyecto (0041). Lo que se arregla es a QUIÉN se le ofrece.
 *
 * Al de oficina se le muestra CON el rótulo en el nombre. Un banco de pruebas que se ve idéntico a
 * una campaña real es el mismo accidente esperando a otra persona; el rótulo va pegado al nombre
 * —dentro del `<option>`— porque un desplegable cerrado no muestra nada más que eso.
 *
 * Función pura y exportada: es una regla de visibilidad, y una regla que solo vive dentro de un
 * componente no se puede verificar.
 */
export function campaniasParaElegir(
  campanias: readonly CampaniaElegible[], roles: readonly string[],
): CampaniaElegible[] {
  const deCampo = soloTrabajaEnCampo(roles);
  const salida: CampaniaElegible[] = [];
  for (const c of campanias) {
    if (!c.esBancoPruebas) { salida.push(c); continue; }
    if (deCampo) continue;
    salida.push({ ...c, nombre: `${c.nombre}${SUFIJO_BANCO_PRUEBAS}` });
  }
  return salida;
}

/** En qué quedó la visita a un puesto. `anulado` solo lo pone el panel, nunca el celular. */
export type EstadoVisita = 'medido' | 'no_pude' | 'anulado';

/**
 * Cómo se le dice al auditor en qué quedó un puesto.
 *
 * Vive acá y no en cada pantalla porque lo usan DOS: la lista de avance del mercado
 * (`FichaPuesto`) y «Mi historial». Con el texto escrito a mano en las dos, el día que alguien
 * cambie una se queda la otra, y el mismo registro pasa a llamarse distinto según dónde se mire.
 *
 * `anulado` tiene su propio texto y no cae en el de «no pude»: son cosas opuestas —una la decidió
 * el auditor en el puesto, la otra la decidió el panel después— y confundirlas le haría creer que
 * ese puesto quedó medido cuando ya no cuenta para nadie.
 */
export function etiquetaEstadoVisita(estado: EstadoVisita): string {
  if (estado === 'medido') return 'Medido';
  if (estado === 'anulado') return 'Anulada';
  return 'No pude medirlo';
}

/**
 * Cómo se nombra un puesto en una lista.
 *
 * El número NO alcanza para reconocerlo: es el del letrero, se repite y se saltea. Por eso, cuando
 * el auditor escribió una referencia, va pegada al número.
 *
 * El parámetro se declara por su FORMA y no con el tipo de una de las dos fuentes: el mismo puesto
 * llega desde el servidor y desde la cola del celular, y atarlo a una obligaría a la otra a
 * convertirse antes solo para poder escribir su título.
 */
export function tituloDePuesto(
  v: { numeroRecorrido: number; nombrePuesto: string | null },
): string {
  return v.nombrePuesto === null
    ? `Puesto ${v.numeroRecorrido}`
    : `Puesto ${v.numeroRecorrido} · ${v.nombrePuesto}`;
}

export const MOTIVOS_RECOMENDACION = [
  { valor: 'mas_ganancia', label: 'Me deja más ganancia' },
  { valor: 'sale_rapido', label: 'Sale más rápido' },
  { valor: 'cliente_la_pide', label: 'El cliente la pide' },
  { valor: 'me_la_bonifican', label: 'Me la bonifican' },
  { valor: 'mas_barata', label: 'Es la más barata' },
] as const;

export const MOTIVOS_NO_PUDE = [
  { valor: 'cerrado', label: 'Estaba cerrado' },
  { valor: 'no_quiso', label: 'No quiso atender' },
  { valor: 'no_lo_encontre', label: 'No lo encontré' },
] as const;

/** De quién es la promoción. La separación que el cliente quiere leer. */
export const DE_QUIEN = [
  { valor: 'propia', label: 'Nuestra (Softys)' },
  { valor: 'competencia', label: 'De la competencia' },
] as const;

export interface MarcaLevantamiento {
  id: string;
  nombre: string;
  /** Qué categoría mide. Es lo que decide si esta marca entra en la ficha de ESTE puesto. */
  categoria: CategoriaProducto;
  /**
   * true = es de Softys; false = competencia.
   *
   * OJO: **Suave estuvo clasificada como competencia en el primer análisis** y la 0060 la pone como
   * propia; está pendiente de que el usuario lo confirme. Todo reporte de «share propio vs
   * competencia» depende de este booleano.
   */
  esPropia: boolean;
  /** true = la fila «Marca económica»: su nombre real lo declara el auditor en cada visita. */
  esReferenciaBarata: boolean;
  /** false = entra en la cuadrícula de presencia pero no se le propone precio. */
  pidePrecio: boolean;
  /** true = al anotar su precio hay que preguntar cuántas unidades trae el paquete. */
  pideUnidades: boolean;
  precioMin: number | null;
  /** Rango del PRECIO POR UNIDAD (0062). Distinto de `precioMin`/`precioMax`, que son del paquete. */
  ppuMin: number | null;
  ppuMax: number | null;
  precioMax: number | null;
  sort: number;
}

/**
 * Una presentación del catálogo: «x4 rollos», «XXG x44», «x45 metros».
 *
 * Reemplaza al texto libre `presentacion`, que en producción produjo NUEVE escrituras distintas
 * para dos presentaciones —«4», «X4», «XL X4», «XL x 4», «Elite XL X4» para el paquete de 4 de
 * Elite XL, y «X4», «X4 rollos», «X4 Verde», «Suave x 4 Negro» para el de Suave—. Un campo que se
 * CUENTA no puede ser texto libre; es la misma razón por la que `MARCAS_RECOMENDABLES` es una lista
 * cerrada.
 */
export interface PresentacionMarca {
  id: string;
  marcaId: string;
  nombre: string;
  /**
   * Cuántas unidades vendibles trae. `null` = no aplica o no se sabe, y es un valor legítimo:
   * «x45 metros» dice el largo del rollo, no cuántos rollos trae el paquete. Con `null` el precio
   * por unidad no se calcula, que es mejor que calcularlo con un número inventado.
   */
  unidades: number | null;
  orden: number;
}

/**
 * Las marcas que le tocan a este puesto: las de las categorías que declaró.
 *
 * Función pura y exportada porque es LA regla del rediseño y tiene que poder verificarse sin montar
 * la pantalla. La ficha carga las marcas de la campaña UNA vez y filtra con esto cada vez que la
 * auditora cambia las categorías: el filtro tiene que funcionar sin señal, de pie en el mercado, y
 * un viaje al servidor por cada tilde no funciona ahí.
 *
 * Sin categorías devuelve la lista VACÍA y no la lista entera. Es a propósito: «todavía no dijo qué
 * vende» y «vende de todo» son cosas distintas, y devolver todo haría que la cuadrícula apareciera
 * completa antes de la pregunta que existe para recortarla.
 */
export function marcasDeCategorias(
  marcas: readonly MarcaLevantamiento[], categorias: readonly CategoriaProducto[],
): MarcaLevantamiento[] {
  return marcas.filter((m) => categorias.includes(m.categoria));
}

// ---------------------------------------------------------------------------
// Filas repetibles: precios y promociones
// ---------------------------------------------------------------------------

/**
 * Una fila de precio. Se pueden agregar TODAS las que haga falta.
 *
 * El modelo anterior tenía un precio por marca. Un puesto tiene el mismo pañal en tres tamaños
 * a precios distintos: con una sola fila había que elegir cuál reportar y perder las otras dos,
 * y el precio por unidad —la métrica del canal— salía de una muestra arbitraria.
 */
export interface FilaPrecio {
  /** Id local, para la clave de React y para amarrar su foto. No viaja al servidor. */
  id: string;
  /** Marca de la lista. Vacío = es una marca que no está en la cuadrícula. */
  marcaId: string;
  /** Nombre tecleado, para la «Marca económica» o para una marca fuera de la lista. */
  marcaLibre: string;
  /**
   * La presentación del CATÁLOGO (`levantamiento_presentaciones`). Vacío = ninguna elegida, y
   * entonces manda `presentacion` de texto libre.
   *
   * Las dos conviven a propósito: el catálogo cubre lo conocido —y trae las `unidades`, que es lo
   * que convierte el precio en precio por unidad sin que nadie las teclee— y el texto libre cubre
   * lo que no está en él, igual que `marcaLibre` cubre la marca que no está en la cuadrícula.
   */
  presentacionId: string;
  /** Qué presentación es, tecleada: «XL x40», «paquete chico»… Solo si no eligió una del catálogo. */
  presentacion: string;
  precio: string;
  /**
   * Si `precio` es el del paquete o el de una unidad.
   *
   * NACE EN `paquete` Y NO VACÍO. Es una decisión, no un descuido: el Excel apunta a una ficha de
   * 4-5 minutos y la 0052 ya recortó dos preguntas de precio por ese motivo; obligar a contestar
   * una pregunta más por CADA fila de precio, en un recorrido de treinta puestos, es el tipo de
   * costo que hace que la auditora deje de medir a la segunda semana. El valor por omisión es el
   * que la columna ya tenía de hecho (`precio_paquete`), así que nada cambia de significado, y el
   * caso raro —el precio unitario— se marca con un toque. Lo que atrapa a quien no lo tocó cuando
   * debía es el aviso de `avisoPrecioPorUnidad`, que se muestra siempre.
   */
  precioPor: PrecioPor;
  unidades: string;
  /**
   * Las fotos de ESTA fila, en el orden en que se tomaron. Lista vacía = ninguna.
   *
   * Era una sola (`foto: Blob | null`) hasta el 2026-08-19, cuando el usuario pidió *«en todos
   * los campos donde se puede subir fotos, se puedan subir más de una»*. Con una sola, elegir una
   * segunda foto del mismo precio **reemplazaba a la primera sin avisar**: el góndola completo y
   * el detalle de la etiqueta no cabían en una toma, y lo que se conservaba era la última.
   *
   * El ORDEN no es decorativo: es el índice con el que `uploadVisitaPhoto` arma la ruta de cada
   * foto, así que es lo que hace que un reintento pise su propio archivo en vez de dejar copias
   * sueltas en el Storage.
   */
  fotos: Blob[];
}

export interface FilaPromocion {
  id: string;
  marcaId: string;
  marcaLibre: string;
  deQuien: 'propia' | 'competencia' | '';
  descripcion: string;
  /** Igual que en `FilaPrecio.fotos`: varias, en orden, y lista vacía cuando no hay ninguna. */
  fotos: Blob[];
}

export const filaPrecioVacia = (id: string, marcaId = ''): FilaPrecio => ({
  id, marcaId, marcaLibre: '', presentacionId: '', presentacion: '', precio: '',
  precioPor: PRECIO_POR_PREDETERMINADO, unidades: '', fotos: [],
});

export const filaPromocionVacia = (id: string): FilaPromocion => ({
  id, marcaId: '', marcaLibre: '', deQuien: '', descripcion: '', fotos: [],
});

// ---------------------------------------------------------------------------
// Observaciones libres, una por sección
// ---------------------------------------------------------------------------

/**
 * Las secciones que aceptan una observación libre.
 *
 * Una por sección y no una sola al final: una nota suelta al pie no dice de qué pregunta habla,
 * y al leer 300 fichas «estaba casi vacío» puede ser el surtido, el precio o la promoción.
 *
 * `¿En qué mercado estás?` no tiene: no es una pregunta sobre el puesto, y una nota sobre el
 * mercado ya tiene su lugar en el cierre de jornada.
 */
export const SECCIONES_OBSERVABLES = [
  'obsPuesto', 'obsGiro', 'obsCliente', 'obsCuadricula',
  'obsPrecios', 'obsPromocion', 'obsRecomendacion', 'obsEvidencia',
] as const;

export type SeccionObservable = (typeof SECCIONES_OBSERVABLES)[number];

/** El nombre de la columna del servidor para cada sección. */
export const COLUMNA_DE_SECCION: Record<SeccionObservable, string> = {
  obsPuesto: 'obs_puesto',
  obsGiro: 'obs_giro',
  obsCliente: 'obs_cliente',
  obsCuadricula: 'obs_cuadricula',
  obsPrecios: 'obs_precios',
  obsPromocion: 'obs_promocion',
  obsRecomendacion: 'obs_recomendacion',
  obsEvidencia: 'obs_evidencia',
};

/**
 * Cómo se llama cada sección observable EN PANTALLA.
 *
 * Estaban escritos a mano dentro de `listVisitasBandeja`, en una lista paralela a
 * `SECCIONES_OBSERVABLES` que había que acordarse de ampliar. Ahora la bandeja los lee de acá y el
 * modal de corrección también: una nota que se rotula «Cuadrícula» en un lado y «Marca por marca»
 * en otro obliga a adivinar si hablan de la misma pregunta.
 */
export const TITULO_DE_SECCION: Record<SeccionObservable, string> = {
  obsPuesto: '¿Qué puesto es?',
  obsGiro: '¿Qué vende?',
  obsCliente: '¿A quién le vende?',
  obsCuadricula: 'Cuadrícula',
  obsPrecios: 'Precios',
  obsPromocion: 'Promociones',
  obsRecomendacion: 'Recomendación',
  obsEvidencia: 'Evidencia',
};

// ---------------------------------------------------------------------------
// El borrador de la pantalla
// ---------------------------------------------------------------------------

export interface BorradorFicha {
  /**
   * Pregunta 2 del Excel, «¿Quién eres?». **La pantalla ya no la muestra**: el usuario la quitó
   * el 19-ago porque quien mide sale de la sesión. Se conserva el campo —y su columna— para
   * volver a encenderla en un renglón si algún día se comparte una cuenta y hace falta.
   *
   * OJO: mientras el sprint corra con cuentas genéricas, `worker_id` dice con qué acceso se
   * entró, no quién midió (S9 en PENDIENTES).
   */
  auditorDeclarado: string;
  /**
   * El número REAL del puesto dentro del mercado: el del letrero, el de su ubicación física.
   * Lo confirmó el usuario el 19-ago. **No es el correlativo del recorrido**, y por eso la
   * pantalla dejó de autoincrementarlo: nace vacío y lo escribe el auditor mirando el puesto.
   *
   * ⚠️ El nombre `numeroRecorrido` —y la columna `numero_recorrido`— quedó del modelo viejo, en
   * el que sí se creía que era el orden del recorrido. **NO se renombró a propósito**:
   * renombrarlo tocaría la RPC de producción, que además no está versionada en este repo, y
   * cambiar un nombre no vale arriesgar el único camino por el que entran las fichas. Es deuda
   * anotada, no un descuido: el identificador miente, la etiqueta que ve el auditor no.
   */
  numeroRecorrido: string;
  /**
   * Referencia libre para reconocer el puesto después («el de doña Rosa», «esquina, pasillo 3»).
   * Cadena vacía = el auditor no la escribió, que es lo normal: es OPCIONAL y no es una pregunta
   * del cuestionario.
   *
   * Existe porque `numeroRecorrido` NO identifica: es el número real del letrero, se repite y se
   * saltea (en Mercado Unicachi Norte hay dos puestos con el n.º 1 el mismo día). Sin esto, la
   * lista de avance del celular muestra dos veces «Puesto 1» y no hay forma de saber si son dos
   * puestos distintos o la misma ficha cargada dos veces.
   */
  nombrePuesto: string;
  estado: 'medido' | 'no_pude';
  motivoNoPude: string;
  /**
   * QUÉ VENDE este puesto. Es la respuesta que recorta todo lo demás: la cuadrícula, las filas de
   * precio y las promociones se arman solo con las marcas de estas categorías.
   *
   * Varias a la vez, y por eso es una lista y no una opción: un puesto de mercado vende papel Y
   * pañales con toda naturalidad —12 de las 22 visitas cargadas declararon «ambos»—, y obligarlo a
   * elegir una sola fue justamente lo que hizo que `giro` no sirviera para filtrar nada.
   */
  categorias: CategoriaProducto[];
  /**
   * `giro`, la pregunta vieja. **Se conserva vacía**: `visitaDesdeBorrador` la deriva de
   * `categorias` con `giroDesdeCategorias` cuando la pantalla no la llena, porque el CHECK
   * `levantamiento_visita_completa` la sigue exigiendo en el servidor. Una pantalla que todavía la
   * pregunte la escribe acá y esa respuesta manda.
   */
  giro: string;
  clientePrincipal: string;
  /**
   * Nombre y teléfono del PUESTERO. Los dos OPCIONALES: se piden al final, cuando la persona ya
   * dejó medir, y un puestero que no quiere darlos no puede bloquear una ficha que ya está completa.
   *
   * ⚠️ Es dato personal de un TERCERO —no es usuario del sistema y no aceptó nada—. En el servidor
   * la lectura de esas dos columnas está revocada a `authenticated` (0060) y solo se puede leer por
   * una función auditada. Acá viven en claro nada más el rato que dura la ficha; no se muestran en
   * ninguna lista ni se exportan.
   */
  contactoNombre: string;
  contactoTelefono: string;
  presencias: Record<string, Presencia | ''>;
  precios: FilaPrecio[];
  hayPromocion: boolean | null;
  promociones: FilaPromocion[];
  /** Una de `MARCAS_RECOMENDABLES` o `RECOMENDADA_OTRA`. */
  marcaRecomendada: string;
  marcaRecomendadaOtra: string;
  motivoRecomendacion: string;
  recomendacionDeclinada: boolean;
  vioMarcaDesconocida: boolean | null;
  hallazgoNombre: string;
  /**
   * Las fotos del frente del puesto, en orden. **Al menos una es obligatoria** (ver
   * `loQueFaltaEnFicha`); las demás son las que el auditor quiera sumar.
   *
   * Es una lista desde el 2026-08-19 por el mismo pedido que las de las filas: el frente de un
   * puesto de mercado no entra en una sola toma, y con un único `Blob` la segunda foto borraba a
   * la primera en silencio.
   */
  fotoPuesto: Blob[];
  /** Fotos del empaque de la marca desconocida. Solo viajan si `vioMarcaDesconocida`. */
  fotoHallazgo: Blob[];
  observaciones: Record<SeccionObservable, string>;
}

/**
 * ¿Esta fila de precio tiene que ofrecer el campo «¿cuántas unidades trae?»?
 *
 * Las tres condiciones y su porqué:
 *   · **La marca las pide** (`pideUnidades`), o la fila es de una marca ESCRITA A MANO. Este
 *     segundo caso es el que faltaba: una fila libre no tiene marca del catálogo, así que
 *     `marca?.pideUnidades` era `undefined` y el campo no se pintaba nunca — y sin unidades no hay
 *     precio por unidad. Mientras toallas higiénicas no tuvo ninguna marca configurada, TODAS sus
 *     filas eran tecleadas y la categoría entera no podía producir un solo PPU (bloqueo 3 de F15).
 *     Hoy tiene tres marcas, pero el agujero seguía abierto para cualquier marca fuera de lista.
 *   · **El precio declarado es el del PAQUETE.** Si es el de una unidad no hay nada que dividir, y
 *     preguntarlo obligaría a inventar el contenido de un paquete que no se está mirando.
 *   · **La presentación del catálogo no las trae ya.** Volver a preguntar lo que «XXG x44» ya dice
 *     es una pregunta de más por fila.
 *
 * OFRECER no es EXIGIR: para la fila tecleada el campo aparece y queda opcional. `loQueFaltaEnFicha`
 * solo lo reclama cuando la marca del catálogo declara `pideUnidades`, porque de una marca que
 * nadie configuró no se sabe si el número aplica siquiera.
 */
export function filaOfreceUnidades(
  marca: MarcaLevantamiento | undefined,
  precioPor: PrecioPor,
  unidadesDelCatalogo: number | null,
): boolean {
  const laPideLaMarca = marca === undefined || marca.pideUnidades;
  return laPideLaMarca && precioPor === 'paquete' && unidadesDelCatalogo === null;
}

export function borradorVacio(marcas: MarcaLevantamiento[]): BorradorFicha {
  const presencias: Record<string, Presencia | ''> = {};
  for (const m of marcas) presencias[m.id] = '';
  return {
    auditorDeclarado: '',
    numeroRecorrido: '', nombrePuesto: '', estado: 'medido', motivoNoPude: '',
    categorias: [], giro: '', clientePrincipal: '',
    contactoNombre: '', contactoTelefono: '',
    presencias,
    precios: [],
    hayPromocion: null, promociones: [],
    marcaRecomendada: '', marcaRecomendadaOtra: '', motivoRecomendacion: '',
    recomendacionDeclinada: false,
    vioMarcaDesconocida: null, hallazgoNombre: '',
    fotoPuesto: [], fotoHallazgo: [],
    observaciones: Object.fromEntries(
      SECCIONES_OBSERVABLES.map((k) => [k, '']),
    ) as Record<SeccionObservable, string>,
  };
}

/**
 * Las marcas a las que la pantalla PROPONE una fila de precio: las que el puesto tiene hoy y
 * tienen precio configurado.
 *
 * Es una propuesta, no un candado: el auditor puede borrar la fila o agregar las que quiera.
 * Preguntar el precio de algo que no trabaja produce un dato inventado —el puestero contesta
 * cualquier cosa para salir del paso—, y el de un quiebre es el de la última vez.
 */
export function marcasQueProponenPrecio(
  marcas: MarcaLevantamiento[], presencias: Record<string, Presencia | ''>,
): MarcaLevantamiento[] {
  return marcas.filter((m) => m.pidePrecio && presencias[m.id] === 'presente');
}

// ---------------------------------------------------------------------------
// Precio por unidad
// ---------------------------------------------------------------------------

/**
 * Si el precio anotado es el del PAQUETE o el de UNA unidad.
 *
 * ⚠️ POR QUÉ EXISTE — el hallazgo del 2026-08-19 verificado fila por fila contra producción. El
 * campo «precio» significaba las dos cosas a la vez y la MISMA auditora lo usó de las dos formas el
 * mismo día: en siete filas de Babysec anotó el precio del paquete (S/44.00 el de 44 unidades,
 * S/72.00 el de 72) y en una anotó S/1.00 declarando 57 unidades, que es lo que cuesta UN pañal
 * suelto. Dividir esa fila da S/0.018 por pañal, un número que no existe.
 *
 * Con una sola columna no había forma de saber cuál era cuál sin mirar el número y adivinar, y
 * adivinar es exactamente lo que este módulo evita en todo lo demás.
 */
export type PrecioPor = 'unidad' | 'paquete';

/** Las dos opciones, con el texto que ve la auditora. El orden pone primero el caso frecuente. */
export const PRECIOS_POR: { valor: PrecioPor; label: string }[] = [
  { valor: 'paquete', label: 'Es el precio del paquete' },
  { valor: 'unidad', label: 'Es el precio de una unidad' },
];

export function etiquetaPrecioPor(p: PrecioPor): string {
  return PRECIOS_POR.find((x) => x.valor === p)?.label ?? p;
}

/**
 * Qué significa un `precio_por` que nadie declaró: **el precio del PAQUETE**.
 *
 * LA DECISIÓN, Y POR QUÉ ESTA Y NO OTRA. Las 37 filas de precio ya cargadas tienen la columna en
 * NULL, porque se escribieron antes de que existiera. Hay tres lecturas posibles y solo una no
 * rompe nada:
 *
 *   · «Paquete» — es lo que significaba el nombre de la columna (`precio_paquete`), es lo que hacía
 *     el código que escribió esas filas, y es lo que el panel ya viene mostrando desde agosto.
 *     Elegirla deja todos los números publicados exactamente donde están.
 *   · «Unidad» — cambiaría en silencio el precio por unidad de 37 filas y con él los promedios que
 *     el cliente ya vio. Un cambio de significado retroactivo que no falla es lo peor de los dos
 *     mundos.
 *   · «No se sabe» — dejaría sin precio por unidad a todo lo medido hasta hoy, que es justamente la
 *     métrica del canal. Perder el dato para no equivocarse en una fila de cada ocho no compensa.
 *
 * La fila de S/1.00 que sí era unitaria queda mal leída, y eso NO se tapa: se ve, porque su precio
 * por unidad cae fuera de 0.30–3.00 y el servidor le pone la bandera `ppu_fuera_de_rango`. Es un
 * dato marcado como sospechoso, que es lo correcto, en vez de un dato corregido a mano por alguien
 * que no estuvo en el puesto.
 *
 * La MISMA regla está escrita del lado del servidor en `levantamiento_precio_unitario` (0060). Son
 * dos copias en dos lenguajes y no hay forma de evitarlo —una la corre el celular sin señal, la
 * otra el servidor—; si una cambia, cambian las dos.
 */
export const PRECIO_POR_PREDETERMINADO: PrecioPor = 'paquete';

/**
 * Las unidades que hay que usar para dividir: lo TECLEADO manda sobre el catálogo.
 *
 * `unidadesPaquete` es lo que la auditora contó en ESE paquete, frente al puestero; la presentación
 * es el valor esperado del catálogo. Cuando los dos existen y no coinciden, el que estuvo ahí es el
 * primero — un fabricante cambia el contenido de un paquete y el catálogo se entera meses después.
 * La presentación cubre las filas donde no lo tecleó, que con el desplegable nuevo pasan a ser la
 * mayoría.
 */
export function unidadesEfectivas(
  unidadesPaquete: number | null, unidadesPresentacion: number | null,
): number | null {
  if (unidadesPaquete !== null && Number.isFinite(unidadesPaquete) && unidadesPaquete > 0) {
    return unidadesPaquete;
  }
  if (unidadesPresentacion !== null && Number.isFinite(unidadesPresentacion)
      && unidadesPresentacion > 0) {
    return unidadesPresentacion;
  }
  return null;
}

/**
 * El precio por unidad. **Nunca se guarda: se calcula al leer**, así que corregir una unidad
 * recalcula todo solo (regla de la 0047 que sigue vigente).
 *
 * Los casos borde, y qué error concreto evita cada uno:
 *   · `precioPor = 'unidad'` → devuelve el precio TAL CUAL. Dividirlo otra vez es el error que
 *     hacía la bandera del servidor antes de la 0060: acusaba de precio raro justo a la auditora
 *     que declaró bien que era el precio de un pañal suelto.
 *   · `precioPor = null` → se lee como `paquete`. Ver `PRECIO_POR_PREDETERMINADO`.
 *   · unidades `null`, `0`, negativas o `NaN` → `null`, nunca el precio a secas. Devolver el precio
 *     sería afirmar que cada rollo cuesta lo que cuesta el paquete.
 *   · precio `0`, negativo o `NaN` → `null`. Un cero saldría como un precio por unidad de cero y se
 *     promediaría con los demás; `null` sale del promedio, que es lo correcto para «no se sabe».
 */
export function precioUnitario(
  precio: number, precioPor: PrecioPor | null, unidades: number | null,
): number | null {
  if (!Number.isFinite(precio) || precio <= 0) return null;
  if ((precioPor ?? PRECIO_POR_PREDETERMINADO) === 'unidad') return precio;
  if (unidades === null || !Number.isFinite(unidades) || unidades <= 0) return null;
  return precio / unidades;
}

/**
 * La forma vieja, de dos argumentos: precio del paquete entre lo que trae.
 *
 * Se conserva porque la usan pantallas que todavía no conocen `precio_por`, y **es exactamente
 * `precioUnitario(precio, 'paquete', unidades)`** — un envoltorio, no una segunda implementación:
 * dos cuentas del mismo número se desincronizan y la que se olvida de actualizar es la que sigue
 * pintando el número viejo.
 */
export function precioPorUnidad(precio: number, unidades: number | null): number | null {
  return precioUnitario(precio, 'paquete', unidades);
}

/**
 * ⚠️ LA BANDA VIEJA. Se conserva SOLO como respaldo de lectura para las pantallas que todavía no
 * tienen la marca a mano, y **no se debe usar para avisar**: dejó de servir.
 *
 * Historia, porque es la lección: la banda 0.30–3.00 se verificó de verdad contra los datos que
 * había («los paquetes de pañales dan entre 0.90 y 1.05 por unidad, y el papel higiénico da entre
 * 0.63 y 2.00»), y el comentario original ya anticipaba su final: «el día que dejen de caber, se
 * parte por categoría en vez de ensancharse, porque una banda ancha no avisa de nada».
 *
 * Ese día llegó el 2026-08-22, medido con 144 visitas y 671 precios: el precio por unidad legítimo
 * del canal va de **0.02** (un pañal suelto) a **11.00** (un rollo de papel toalla), y la bandera
 * `ppu_fuera_de_rango` estaba apareciendo en **81 de 143 visitas (57 %)** — 260 de las 275 filas
 * fuera de la banda lo estaban por PASARSE de 3.00, con precios correctos. Una alerta que salta en
 * más de la mitad de los casos deja de ser una alerta.
 */
export const PPU_MIN = 0.3;
export const PPU_MAX = 3;

/**
 * El rango de precio por unidad de una marca. `null` en cualquiera de los dos = ese lado no se
 * evalúa: no se puede decir que un precio está fuera de un rango que nadie definió.
 *
 * Los nombres son los de `MarcaLevantamiento` a propósito: así los dos llamadores —la ficha, que
 * tiene la marca del catálogo, y el panel, que tiene la fila de precio— pasan lo que ya tienen sin
 * armar un objeto intermedio, que es donde se cuelan los campos cruzados.
 */
export interface RangoPpu { ppuMin: number | null; ppuMax: number | null }

/**
 * `true` si el precio por unidad sale del rango DE SU MARCA (migración 0062).
 *
 * El rango llega por parámetro y no se lee de una constante a propósito: es dato de la marca, igual
 * que `precioMin`/`precioMax`, porque cambia por categoría y por cliente. Un pañal y un rollo de
 * papel toalla no pueden compartir banda.
 *
 * **Sin rango no hay aviso.** Eso hace visible la falta de calibración —una marca nueva no dispara
 * nunca— en vez de esconderla detrás de una banda prestada que no le corresponde.
 */
export function ppuFueraDeRango(ppu: number | null, rango?: RangoPpu | null): boolean {
  if (ppu === null) return false;
  if (!rango) return false;
  if (rango.ppuMin !== null && ppu < rango.ppuMin) return true;
  return rango.ppuMax !== null && ppu > rango.ppuMax;
}

/**
 * Los rangos de precio POR CATEGORÍA, para la fila cuya marca no está en la cuadrícula.
 *
 * ⚠️ ES UN RESPALDO, NO LA FUENTE. El rango de verdad vive en la marca (`precioMin`/`precioMax`,
 * 0051) porque cambia por marca y por cliente, y `avisoRangoPrecio` lo lee de ahí. Esto solo cubre
 * el caso en el que NO HAY marca de la que leerlo: la auditora tecleó un nombre a mano, que con las
 * categorías nuevas deja de ser raro —`toallas_higienicas` no tiene ni una marca configurada, así
 * que todo precio de toallas va a ser una marca tecleada—.
 *
 * Los valores son los mismos que la 0060 escribe en las marcas. Si allá cambian, acá también.
 */
export const RANGO_PRECIO_POR_CATEGORIA: Record<
  CategoriaProducto, { min: number; max: number }
> = {
  // Observado S/2.50–S/8.00. El techo deja lugar a un paquete de 12 rollos que no cayó en la
  // muestra: un aviso que salta con un precio legítimo se aprende a ignorar, y ahí se pierde entero.
  papel_higienico: { min: 2, max: 20 },
  // Observado S/9.00 (paquete de 10) a S/72.00 (paquete de 72). El piso baja a 1 porque con
  // `precio_por` el precio de UN pañal pasa a ser una respuesta válida, y un piso de 10 marcaría
  // como error justo lo que ahora se quiere poder declarar.
  panales: { min: 1, max: 90 },
  // ⚠️ ESTIMACIÓN SIN DATO: ninguna visita midió toallas y no hay ninguna marca configurada de esta
  // categoría. Sale del precio de mostrador, no de este levantamiento. El primero que mida una va a
  // ver un aviso que todavía no está calibrado.
  toallas_higienicas: { min: 1, max: 30 },
};

/**
 * Texto del aviso del precio por unidad, o `null` si no hay nada que decir.
 *
 * Se muestra SIEMPRE, no solo cuando está mal: es con ese número que la propia auditora detecta que
 * anotó la unidad en vez del paquete. Un mensaje de error no le da ese dato.
 *
 * Los dos últimos parámetros son OPCIONALES y con el valor que reproduce el comportamiento anterior
 * (`paquete`, sin presentación). No es comodidad: la pantalla móvil vieja llama a esto con dos
 * argumentos, y si la firma exigiera cuatro dejaría de compilar en la misma tanda en la que se toca
 * el esquema — o sea, se tocarían las dos cosas a la vez y no habría a qué volver.
 */
export function avisoPrecioPorUnidad(
  precio: string, unidades: string,
  precioPor: PrecioPor | null = null,
  unidadesPresentacion: number | null = null,
  rango: RangoPpu | null = null,
): string | null {
  const tecleadas = unidades.trim() === '' ? null : Number(unidades);
  const ppu = precioUnitario(
    Number(precio), precioPor, unidadesEfectivas(tecleadas, unidadesPresentacion),
  );
  if (ppu === null) return null;
  const redondo = `S/ ${ppu.toFixed(2)}`;
  // El número se muestra SIEMPRE, con rango o sin él: es con ese número que la propia auditora
  // detecta que anotó la unidad en vez del paquete. Lo que el rango decide es si además se pregunta.
  if (!ppuFueraDeRango(ppu, rango)) {
    return (precioPor ?? PRECIO_POR_PREDETERMINADO) === 'unidad'
      ? `Anotaste ${redondo} por unidad.`
      : `Son ${redondo} por unidad.`;
  }
  // Los dos avisos apuntan al error OPUESTO según lo que declaró, porque son dos equivocaciones
  // distintas y decirle la que no es lo manda a revisar el campo correcto.
  return (precioPor ?? PRECIO_POR_PREDETERMINADO) === 'unidad'
    ? `Eso es ${redondo} por unidad. ¿Es correcto? Suele pasar cuando se anota el precio del paquete y se marca «es el precio de una unidad».`
    : `Eso da ${redondo} por unidad. ¿Es correcto? Suele pasar cuando se anota el precio de la unidad en vez del paquete.`;
}

/** Aviso de rango del precio del paquete, o `null` si está dentro (o no hay rango definido). */
export function avisoRangoPrecio(
  m: MarcaLevantamiento | undefined, precio: string,
): string | null {
  if (!m) return null;
  const v = Number(precio);
  if (precio.trim() === '' || !Number.isFinite(v)) return null;
  if (m.precioMin !== null && v < m.precioMin) {
    return `S/ ${v.toFixed(2)} es más bajo de lo esperado para ${m.nombre} (desde S/ ${m.precioMin.toFixed(2)}).`;
  }
  if (m.precioMax !== null && v > m.precioMax) {
    return `S/ ${v.toFixed(2)} es más alto de lo esperado para ${m.nombre} (hasta S/ ${m.precioMax.toFixed(2)}).`;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Validación
// ---------------------------------------------------------------------------

/**
 * Qué le falta a la ficha. `null` = está lista.
 *
 * La obligatoriedad es la del Excel: **los precios NO son obligatorios** (preguntas 7 a 13 van
 * marcadas "No"). Un puestero apurado que no deja anotar precios no puede bloquear la ficha
 * entera; lo que sí es obligatorio es el perfil, la cuadrícula, la recomendación y la foto.
 *
 * Devuelve UNA sola cosa, la primera que falta en el orden del formulario: una lista de seis
 * errores en la pantalla de un celular no se lee, se cierra.
 */
export function loQueFaltaEnFicha(
  b: BorradorFicha, marcas: MarcaLevantamiento[],
  presentaciones: readonly PresentacionMarca[] = [],
): string | null {
  // Desde el 19-ago el campo NACE VACÍO (dejó de autoincrementarse), así que este chequeo pasó
  // de ser teórico a ser el único que impide guardar una ficha sin número. Si se colara, la
  // conversión a `int` de la RPC recibiría `NaN` o vacío y reventaría con un error de Postgres
  // en inglés, en un celular, dentro de un mercado sin señal.
  const numeroPuesto = Number(b.numeroRecorrido);
  if (b.numeroRecorrido.trim() === '' || !Number.isInteger(numeroPuesto) || numeroPuesto < 1) {
    return 'Falta el número del puesto, tal como está en el mercado (1 o más).';
  }

  // Una visita "no pude" termina acá: es un registro válido y completo con solo su motivo.
  if (b.estado === 'no_pude') {
    return b.motivoNoPude === '' ? 'Dinos por qué no pudiste medir este puesto.' : null;
  }

  // ⚠️ ACEPTA LAS DOS FORMAS DE CONTESTAR «qué vende», y es a propósito. La pantalla nueva llena
  // `categorias` y no pregunta `giro`; la que está hoy en producción hace lo contrario. Exigir
  // `categorias` a secas dejaría la ficha actual sin poder guardar en el mismo momento en que se
  // aplica la migración, y una ficha que no guarda deja a alguien de pie en un mercado sin salida —
  // la misma razón por la que `registrar_visita` acepta el payload viejo y el nuevo (BITACORA §18).
  // El día que la ficha nueva esté en todos los celulares, esto pasa a exigir `categorias` a secas.
  if (b.categorias.length === 0 && b.giro === '') {
    return 'Falta qué vende este puesto.';
  }
  if (b.clientePrincipal === '') return 'Falta a quién le vende más este puesto.';

  const sinResponder = marcas.filter(
    (m) => b.presencias[m.id] === '' || b.presencias[m.id] === undefined,
  );
  if (sinResponder.length > 0) {
    return `Falta marcar qué pasa con ${sinResponder[0].nombre} en la cuadrícula.`;
  }

  // Los precios son opcionales, pero una fila EMPEZADA tiene que estar completa: media fila
  // guardada es un dato que parece existir y no se puede usar.
  for (const p of b.precios) {
    const vacia = p.precio.trim() === '' && p.presentacion.trim() === ''
      && p.presentacionId === '' && p.marcaLibre.trim() === '' && p.unidades.trim() === '';
    if (vacia) continue;
    const nombre = nombreDeFila(p, marcas);
    if (p.marcaId === '' && p.marcaLibre.trim() === '') {
      return 'Falta de qué marca es uno de los precios que anotaste.';
    }
    if (p.precio.trim() === '' || !(Number(p.precio) > 0)) {
      return `Falta el precio de ${nombre}.`;
    }
    const marca = marcas.find((m) => m.id === p.marcaId);
    // Las unidades dejan de ser obligatorias en DOS casos nuevos, y los dos son casos en los que
    // pedirlas sería pedir un dato que ya se tiene o que no significa nada:
    //   · La presentación elegida del catálogo ya las trae («XXG x44» → 44). Volver a preguntarlas
    //     es una pregunta de más por fila de precio, que es lo que la 0052 recortó.
    //   · El precio declarado es el de UNA unidad: no hay nada que dividir. Con la regla vieja, la
    //     auditora que anotó bien S/1.00 el pañal quedaba obligada a inventar cuántos trae un
    //     paquete que no estaba mirando.
    const delCatalogo = presentaciones.find((x) => x.id === p.presentacionId);
    const necesitaUnidades = marca?.pideUnidades === true
      && p.precioPor === 'paquete'
      && (delCatalogo === undefined || delCatalogo.unidades === null);
    if (necesitaUnidades && (p.unidades.trim() === '' || !(Number(p.unidades) > 0))) {
      return `Falta cuántas unidades trae el paquete de ${nombre}.`;
    }
  }

  if (b.hayPromocion === null) return 'Falta decir si hay alguna promoción hoy en este puesto.';
  if (b.hayPromocion) {
    const conAlgo = b.promociones.filter(
      (p) => p.descripcion.trim() !== '' || p.marcaId !== '' || p.marcaLibre.trim() !== '',
    );
    if (conAlgo.length === 0) return 'Dijiste que hay promoción: anota al menos una.';
    for (const p of conAlgo) {
      if (p.marcaId === '' && p.marcaLibre.trim() === '') {
        return 'Falta de qué marca es una de las promociones.';
      }
      if (p.deQuien === '') return 'Falta decir si una de las promociones es nuestra o de la competencia.';
      if (p.descripcion.trim() === '') return 'Falta qué está dando una de las promociones.';
    }
  }

  // El bloque de recomendación se puede SALTAR entero, pero a propósito y dejándolo dicho.
  if (!b.recomendacionDeclinada) {
    if (b.marcaRecomendada === '') {
      return 'Falta qué marca ofrece primero (o marca que no quiso responder).';
    }
    if (b.marcaRecomendada === RECOMENDADA_OTRA && b.marcaRecomendadaOtra.trim() === '') {
      return 'Escribe qué otra marca ofrece primero.';
    }
    if (b.motivoRecomendacion === '') return 'Falta por qué ofrece esa marca primero.';
  }

  if (b.vioMarcaDesconocida === true && b.hallazgoNombre.trim() === '') {
    return 'Falta el nombre de la marca que no conoces, tal como se lee en el empaque.';
  }

  // Va al final porque es lo último del formulario y lo más caro de repetir: si el aviso de la
  // foto saliera primero, la persona la toma y recién ahí descubre que le faltaban tres campos.
  //
  // Basta UNA. El campo pasó a ser una lista el 19-ago, y la exigencia siguió siendo la misma que
  // cuando era un solo `Blob`: pedir dos o tres fotos obligatorias alargaría cada ficha de un
  // recorrido de treinta puestos por un dato que ya se cumple con la primera.
  if (b.fotoPuesto.length === 0) return 'Falta la foto del puesto.';

  return null;
}

/** Cómo se llama la marca de una fila, para poder nombrarla en un aviso. */
export function nombreDeFila(
  p: { marcaId: string; marcaLibre: string }, marcas: MarcaLevantamiento[],
): string {
  if (p.marcaLibre.trim() !== '') return p.marcaLibre.trim();
  return marcas.find((m) => m.id === p.marcaId)?.nombre ?? 'esa marca';
}

/**
 * ¿Ese número de puesto ya se registró en este mercado y este día?
 *
 * **AVISA, NO BLOQUEA.** Repetir un puesto es una de las dos fallas que arruinan la medición de
 * un mercado grande (la otra es saltárselo): dos fichas del mismo puesto inflan la muestra y
 * desbalancean la comparación entre mercados, que es todo el valor del ejercicio. Ya pasó en
 * producción —dos puestos con el n.º 1 en Mercado Unicachi Norte el mismo día—. Pero puede haber
 * razones legítimas (un puesto con dos números, un letrero repetido, una corrección), y acá las
 * banderas son calidad de dato a la vista y no candados, igual que en los canjes: bloquear
 * dejaría a alguien parado frente a un puesto real sin poder registrarlo.
 *
 * `numero` llega como el texto crudo del input. Un texto vacío o inválido devuelve `false`, no
 * "repetido": de eso ya se encarga `loQueFaltaEnFicha`, y avisar dos cosas distintas por el
 * mismo campo a la vez solo confunde.
 *
 * `usados` lo arma la pantalla con la UNIÓN de lo que devuelve el servidor para esta cuenta y lo
 * que sigue en la cola de este celular sin subir. Antes salía solo de la cola local, y por eso no
 * detectaba un número repetido desde OTRO celular ni desde una sesión cuya ficha ya se había
 * purgado del teléfono: en producción hay puestos repetidos que la bandera nunca llegó a avisar.
 *
 * Queda un límite honesto, y es de la CUENTA, no de esta función: si otra persona midió el mismo
 * mercado con una cuenta distinta, su número no está acá. La bandera avisa de menos, nunca de más,
 * que es el lado correcto para equivocarse en algo que no bloquea.
 */
export function numeroYaUsado(numero: string, usados: readonly number[]): boolean {
  const n = Number(numero);
  if (numero.trim() === '' || !Number.isInteger(n) || n < 1) return false;
  return usados.includes(n);
}

// ---------------------------------------------------------------------------
// Lo que viaja al servidor
// ---------------------------------------------------------------------------

export interface PrecioParaRegistrar {
  marcaId: string | null;
  marcaDeclarada: string | null;
  /** La presentación del catálogo, o `null` si eligió teclearla. */
  presentacionId: string | null;
  presentacion: string | null;
  precioPaquete: number;
  /**
   * Si `precioPaquete` es el del paquete o el de una unidad.
   *
   * Viaja SIEMPRE con un valor —nunca `null`— aunque el servidor acepte `null`: el `null` está ahí
   * para las 37 filas anteriores a la 0060 y para el bundle viejo que sigue en el caché de algún
   * celular, no para que el frontend nuevo mande fichas que no dicen lo que quisieron decir.
   */
  precioPor: PrecioPor;
  unidadesPaquete: number | null;
  /** Clave de la foto de ESTA fila dentro del lote; `null` si no tomó ninguna. */
  fotoClave: string | null;
}

export interface PromocionParaRegistrar {
  marcaId: string | null;
  marcaLibre: string | null;
  deQuien: 'propia' | 'competencia';
  descripcion: string;
  fotoClave: string | null;
}

export interface VisitaParaRegistrar {
  auditorDeclarado: string | null;
  numeroRecorrido: number;
  /**
   * Referencia libre del puesto. `string | null`, y **nunca `undefined`**.
   *
   * La diferencia no es de estilo: una clave con `undefined` DESAPARECE al serializar el payload a
   * JSON, así que el servidor guardaría `null` sin que nadie se entere de que la ficha traía el
   * dato. Se perdería justo el campo del que depende la lista de avance —lo único que distingue
   * dos puestos con el mismo número de una ficha cargada dos veces— y se perdería en silencio: la
   * visita se registra igual y el hueco recién se ve al leer la base semanas después.
   */
  nombrePuesto: string | null;
  estado: 'medido' | 'no_pude';
  motivoNoPude: string | null;
  /**
   * Las categorías declaradas. Lista VACÍA y **nunca `undefined`**, por lo mismo que
   * `nombrePuesto`: una clave con `undefined` desaparece al serializar el JSON y el servidor
   * guardaría `'{}'` sin que nadie se entere de que la ficha traía el dato — y acá el dato perdido
   * sería justo el que decide qué se le preguntó al puesto.
   */
  categorias: CategoriaProducto[];
  giro: string | null;
  clientePrincipal: string | null;
  /** Del puestero. Dato personal de un tercero; ver `BorradorFicha.contactoNombre`. */
  contactoNombre: string | null;
  contactoTelefono: string | null;
  hayPromocion: boolean | null;
  marcaRecomendada: string | null;
  motivoRecomendacion: string | null;
  recomendacionDeclinada: boolean;
  vioMarcaDesconocida: boolean | null;
  mediciones: { marcaId: string; presencia: Presencia }[];
  precios: PrecioParaRegistrar[];
  promociones: PromocionParaRegistrar[];
  hallazgos: { nombreLeido: string }[];
  observaciones: Partial<Record<SeccionObservable, string>>;
}

const limpio = (s: string): string | null => (s.trim() === '' ? null : s.trim());

/** Claves fijas de las dos fotos que no pertenecen a una fila. */
export const CLAVE_FOTO_PUESTO = 'puesto';
export const CLAVE_FOTO_HALLAZGO = 'hallazgo';

/**
 * Todas las fotos del borrador AGRUPADAS POR CAMPO, con la clave con la que el servidor las va a
 * amarrar y en el orden en que se tomaron.
 *
 * Se devuelven juntas para que la cola sin señal las suba en un solo recorrido y después
 * reemplace cada clave por sus rutas. Sin esto habría que subirlas desde la pantalla, y una ficha
 * con seis fotos en un mercado sin cobertura no se guardaría nunca.
 *
 * DEVUELVE `blobs` (lista) Y NO `blob`: es la forma que espera `FotosDeClave` en `visitaQueue`, y
 * el agrupado importa porque la posición dentro de la clave es lo que va en la ruta de cada foto
 * (`…-<clave>-<n>.jpg`). Si cada foto viajara como su propia entrada con la misma clave, todas
 * caerían en el índice 0 y se pisarían entre ellas con `upsert: true`, sin error.
 *
 * UNA CLAVE SIN FOTOS NO SE EMITE. Es lo que deja que `registrarVisita` traduzca «clave ausente»
 * a la lista vacía sin tener que distinguir casos, y evita subir cero archivos bajo una clave que
 * después nadie busca.
 *
 * El tipo se escribe acá y no se importa de `visitaQueue`: el dominio no depende de la capa
 * offline —es al revés—, y son estructuralmente el mismo objeto.
 */
export function fotosDelBorrador(b: BorradorFicha): { clave: string; blobs: Blob[] }[] {
  const out: { clave: string; blobs: Blob[] }[] = [];
  const agrega = (clave: string, blobs: Blob[]): void => {
    if (blobs.length > 0) out.push({ clave, blobs: [...blobs] });
  };
  agrega(CLAVE_FOTO_PUESTO, b.fotoPuesto);
  // El hallazgo sigue condicionado a la respuesta: si al final dijo que no vio ninguna marca
  // nueva, sus fotos no viajan aunque las haya tomado antes de cambiar de opinión.
  if (b.vioMarcaDesconocida) agrega(CLAVE_FOTO_HALLAZGO, b.fotoHallazgo);
  for (const p of b.precios) agrega(`precio-${p.id}`, p.fotos);
  for (const p of b.promociones) agrega(`promo-${p.id}`, p.fotos);
  return out;
}

/**
 * Observaciones con texto. `soloPuesto` deja pasar únicamente la de «¿Qué puesto es?», que es
 * lo que aplica en una visita `no_pude`: las demás secciones ni se mostraron, y su nota sería
 * un texto huérfano que nadie puede interpretar.
 */
export function observacionesConTexto(
  b: BorradorFicha, soloPuesto = false,
): Partial<Record<SeccionObservable, string>> {
  const out: Partial<Record<SeccionObservable, string>> = {};
  for (const k of SECCIONES_OBSERVABLES) {
    if (soloPuesto && k !== 'obsPuesto') continue;
    const v = (b.observaciones[k] ?? '').trim();
    if (v !== '') out[k] = v;
  }
  return out;
}

/**
 * Convierte el borrador en lo que viaja al servidor.
 *
 * Una visita `no_pude` sale VACÍA de todo lo demás, aunque el auditor haya alcanzado a tocar
 * algo antes de marcarla: mandar respuestas sueltas de un puesto que no se midió las mezclaría
 * con las de los puestos que sí, y nadie podría distinguirlas después.
 */
export function visitaDesdeBorrador(
  b: BorradorFicha, marcas: MarcaLevantamiento[],
): VisitaParaRegistrar {
  const auditor = limpio(b.auditorDeclarado);

  if (b.estado === 'no_pude') {
    return {
      auditorDeclarado: auditor,
      numeroRecorrido: Number(b.numeroRecorrido),
      // El nombre SÍ viaja en una «no pude», al igual que el número: no es una respuesta del
      // cuestionario sino cómo se llama el puesto que no se pudo medir. Vaciarlo dejaría la lista
      // de avance mostrando «Puesto 12 · no pude» sin nada que permita reconocerlo al volver.
      nombrePuesto: limpio(b.nombrePuesto),
      estado: 'no_pude', motivoNoPude: limpio(b.motivoNoPude),
      // Sin categorías y sin contacto: no se midió nada, así que no hay puesto del que decir qué
      // vende, y el contacto de un puesto cerrado o que no quiso atender no se llegó a pedir.
      // Mandar respuestas sueltas de un puesto que no se midió las mezclaría con las de los que sí.
      categorias: [], contactoNombre: null, contactoTelefono: null,
      giro: null, clientePrincipal: null, hayPromocion: null,
      marcaRecomendada: null, motivoRecomendacion: null, recomendacionDeclinada: false,
      vioMarcaDesconocida: null,
      mediciones: [], precios: [], promociones: [], hallazgos: [],
      observaciones: observacionesConTexto(b, true),
    };
  }

  const recomendada = b.recomendacionDeclinada
    ? null
    : b.marcaRecomendada === RECOMENDADA_OTRA
      ? limpio(b.marcaRecomendadaOtra)
      : limpio(b.marcaRecomendada);

  return {
    auditorDeclarado: auditor,
    numeroRecorrido: Number(b.numeroRecorrido),
    // `limpio` devuelve `null` cuando quedó vacío, y nunca `undefined`: ver el campo en
    // `VisitaParaRegistrar`. Con `undefined` la clave se caería del JSON y el dato se perdería sin
    // que nada falle.
    nombrePuesto: limpio(b.nombrePuesto),
    estado: 'medido',
    motivoNoPude: null,
    categorias: [...b.categorias],
    // `giro` se DERIVA cuando la pantalla no lo preguntó. No es adorno: el CHECK de tabla
    // `levantamiento_visita_completa` exige `giro is not null` en toda visita `medido`, así que una
    // ficha que solo trajera categorías la rechazaría el servidor con un 23514 en inglés, sobre un
    // celular, en un mercado. Lo que la pantalla haya respondido manda sobre lo derivado.
    giro: limpio(b.giro) ?? giroDesdeCategorias(b.categorias),
    clientePrincipal: limpio(b.clientePrincipal),
    contactoNombre: limpio(b.contactoNombre),
    contactoTelefono: limpio(b.contactoTelefono),
    hayPromocion: b.hayPromocion,
    marcaRecomendada: recomendada,
    motivoRecomendacion: b.recomendacionDeclinada ? null : limpio(b.motivoRecomendacion),
    recomendacionDeclinada: b.recomendacionDeclinada,
    vioMarcaDesconocida: b.vioMarcaDesconocida,
    mediciones: marcas
      .filter((m) => b.presencias[m.id] !== '' && b.presencias[m.id] !== undefined)
      .map((m) => ({ marcaId: m.id, presencia: b.presencias[m.id] as Presencia })),
    // Solo las filas con precio: una fila abierta y nunca llenada no es un dato.
    precios: b.precios
      .filter((p) => Number(p.precio) > 0)
      .map((p) => {
        const marca = marcas.find((m) => m.id === p.marcaId);
        return {
          marcaId: p.marcaId === '' ? null : p.marcaId,
          marcaDeclarada: limpio(p.marcaLibre),
          // La presentación del catálogo solo viaja si la fila es de una marca de la lista: sin
          // marca no hay catálogo del que salga, y el servidor descarta el id que no sea de esa
          // marca (0060). Mandarlo igual dejaría el payload apuntando a algo que no le corresponde.
          presentacionId: p.marcaId !== '' && p.presentacionId !== '' ? p.presentacionId : null,
          presentacion: limpio(p.presentacion),
          precioPaquete: Number(p.precio),
          precioPor: p.precioPor,
          // Si la marca las pide, o si la marca se escribió a mano y la auditora las anotó igual.
          // Lo segundo se agregó el 22-ago: sin eso, el número que ella tecleaba en una fila libre
          // se descartaba al armar el payload y esa fila jamás producía un precio por unidad.
          // Sigue sin mandarse cuando la marca del catálogo declara que NO las pide: ahí un número
          // suelto sería ruido que después alguien dividiría creyendo que es un PPU.
          unidadesPaquete: (marca === undefined || marca.pideUnidades) && p.unidades.trim() !== ''
            ? Number(p.unidades) : null,
          // Sin fotos no hay clave: `registrarVisita` traduce `null` a la lista vacía, y declarar
          // una clave que `fotosDelBorrador` no emite dejaría el payload apuntando a un grupo que
          // nunca se subió.
          fotoClave: p.fotos.length > 0 ? `precio-${p.id}` : null,
        };
      }),
    promociones: b.hayPromocion
      ? b.promociones
        .filter((p) => p.descripcion.trim() !== '' && p.deQuien !== '')
        .map((p) => ({
          marcaId: p.marcaId === '' ? null : p.marcaId,
          marcaLibre: limpio(p.marcaLibre),
          deQuien: p.deQuien as 'propia' | 'competencia',
          descripcion: p.descripcion.trim(),
          fotoClave: p.fotos.length > 0 ? `promo-${p.id}` : null,
        }))
      : [],
    hallazgos: b.vioMarcaDesconocida && b.hallazgoNombre.trim() !== ''
      ? [{ nombreLeido: b.hallazgoNombre.trim() }]
      : [],
    observaciones: observacionesConTexto(b),
  };
}

// ---------------------------------------------------------------------------
// Cierre de jornada — el cuestionario aparte, una vez por mercado y día
// ---------------------------------------------------------------------------

export const AFLUENCIAS = [
  { valor: 'alta', label: 'Alta' },
  { valor: 'normal', label: 'Normal' },
  { valor: 'baja', label: 'Baja' },
] as const;

/** «Poca pesca» no es un chiste: la actividad del mercado de Chimbote depende de eso. */
export const FACTORES_EXTERNOS = [
  { valor: 'nada_raro', label: 'Nada raro' },
  { valor: 'lluvia', label: 'Lluvia' },
  { valor: 'paro_bloqueo', label: 'Paro o bloqueo' },
  { valor: 'feriado', label: 'Feriado' },
  { valor: 'poca_pesca', label: 'Poca pesca' },
] as const;

export interface BorradorCierre {
  /** Ver `BorradorFicha.auditorDeclarado`: la pantalla ya no la pregunta. */
  auditorDeclarado: string;
  afluencia: string;
  /** «¿Hoy era día con alguna actividad adicional?» — texto exacto del Excel. */
  actividadAdicional: boolean | null;
  factorExterno: string;
  competenciaActiva: string;
  observacion: string;
}

export function cierreVacio(): BorradorCierre {
  return {
    auditorDeclarado: '', afluencia: '', actividadAdicional: null, factorExterno: '',
    competenciaActiva: '', observacion: '',
  };
}

/**
 * Qué le falta al cierre. `null` = está listo.
 *
 * Las cinco son obligatorias, como las marca el Excel — incluidas las dos de texto. Es un
 * formulario de cinco preguntas que se llena UNA vez al día, no por puesto: ahí el costo de
 * exigirlas es bajo y lo que se gana es que el contexto del día no quede en blanco justo los
 * días raros, que son los que hay que explicar.
 */
export function loQueFaltaEnCierre(c: BorradorCierre): string | null {
  if (c.afluencia === '') return 'Falta cómo estuvo la afluencia hoy.';
  if (c.actividadAdicional === null) return 'Falta decir si hubo alguna actividad adicional.';
  if (c.factorExterno === '') return 'Falta si pasó algo que explique el día.';
  if (c.competenciaActiva.trim() === '') {
    return 'Falta si había impulsadoras o activación de la competencia (escribe «ninguna» si no vio).';
  }
  if (c.observacion.trim() === '') return 'Falta lo último: algo más que quieras dejar dicho.';
  return null;
}
